<template>
  <ACard id="basic-info">
    <template #title>
      <span class="bic__title">
        <i class="material-icons-round bic__title-icon">receipt_long</i>
        기본 정보
      </span>
    </template>

    <div class="bic__grid">
      <div v-if="userType === 'admin' && basicData?.emp_number" class="p-field bic__col-full">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">badge</i>
          직원번호
        </label>
        <input type="text" class="p-input" :value="basicData.emp_number" disabled />
      </div>

      <div class="p-field bic__col-full">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">person</i>
          이름
        </label>
        <input v-model="localData.name" type="text" class="p-input" :maxlength="inputMax.name" placeholder="이름" />
      </div>

      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">mail</i>
          이메일
        </label>
        <input v-model="localData.email" type="email" class="p-input" :maxlength="inputMax.email" placeholder="email@example.com" />
      </div>

      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">call</i>
          전화번호
        </label>
        <input v-model="localData.phone" type="tel" class="p-input" :maxlength="inputMax.phone" placeholder="010-0000-0000" />
      </div>

      <div v-if="userType === 'admin'" class="p-field bic__col-full">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">admin_panel_settings</i>
          역할
        </label>
        <select v-model.number="localData.role_idx" class="p-input" :disabled="rolesLoading">
          <option :value="null">역할을 선택하세요</option>
          <option
            v-for="role in rolesList"
            :key="role.role_idx"
            :value="role.role_idx"
          >{{ role.role_name }} ({{ role.role_code }})</option>
        </select>
      </div>

      <template v-if="userType === 'editor'">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">notes</i>
            메모
          </label>
          <textarea v-model="localData.memo" class="p-input p-input--area" rows="3" :maxlength="inputMax.memo" placeholder="메모..." />
          <p class="p-field__hint">{{ localData.memo.length }}/{{ inputMax.memo }}자</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">translate</i>
            모국어
          </label>
          <select v-model="localData.first_language" class="p-input">
            <option :value="null">선택하세요</option>
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <div class="p-field__hint"><i>info</i>저자의 모국어 설정</div>
        </div>
      </template>

      <template v-if="userType === 'user' && (basicData?.country_name || basicData?.first_language)">
        <div v-if="basicData?.country_name" class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">public</i>
            국가
          </label>
          <input type="text" class="p-input" :value="basicData.country_name" disabled />
        </div>
        <div v-if="basicData?.first_language" class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">translate</i>
            모국어
          </label>
          <input type="text" class="p-input" :value="getLanguageName(basicData.first_language)" disabled />
        </div>
      </template>

      <div v-if="basicData?.created_at" class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">event</i>
          계정 생성일
        </label>
        <input type="text" class="p-input" :value="formatDate(basicData.created_at)" disabled />
      </div>
    </div>

    <template #foot>
      <ABtn kind="primary" icon="save" @click="saveChanges">정보 저장</ABtn>
    </template>
  </ACard>
</template>

<script>
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { EDITOR_INPUT_MAX } from "@/utils/editorAccount";

export default {
  name: "BasicInfoCard",
  components: { ACard, ABtn },
  props: {
    basicData: { type: Object, default: () => ({}) },
    userType: {
      type: String,
      default: 'user',
      validator: (v) => ['admin', 'editor', 'user'].includes(v),
    },
  },
  emits: ["update-field", "save-changes"],
  data() {
    return {
      localData: {
        name: "",
        email: "",
        phone: "",
        memo: "",
        role_idx: null,
        first_language: null,
      },
      rolesList: [],
      rolesLoading: false,
      languageOptions: [
        { code: "ko", name: "한국어" },
        { code: "en", name: "English" },
        { code: "de", name: "Deutsch" },
        { code: "ja", name: "日本語" },
        { code: "zh", name: "中文" },
        { code: "fr", name: "Français" },
        { code: "es", name: "Español" },
        { code: "it", name: "Italiano" },
        { code: "ru", name: "Русский" },
        { code: "pt", name: "Português" },
      ],
    };
  },
  computed: {
    // 입력 상한은 에디터 API 만 알려 왔습니다. 직원·사용자는 값이 없어 maxlength 가 붙지 않습니다.
    inputMax() {
      return this.userType === 'editor' ? EDITOR_INPUT_MAX : {};
    },
  },
  watch: {
    basicData: {
      handler(newData) {
        if (newData) {
          this.localData = {
            name: newData.name || "",
            email: newData.email || "",
            phone: newData.phone || "",
            memo: newData.memo || "",
            role_idx: newData.role_idx || null,
            first_language: newData.first_language || null,
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (this.userType === 'admin') this.loadRoles();
  },
  methods: {
    async loadRoles() {
      this.rolesLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/roles/pagePermission/roles",
          loadingBar: false,
        });
        if (res.status === 200 && res.data?.roles) {
          this.rolesList = res.data.roles.filter((role) => role.is_active === 'Y');
        }
      } catch (error) {
        console.error("역할 목록 로드 실패:", error);
      } finally {
        this.rolesLoading = false;
      }
    },
    saveChanges() {
      const changes = {};
      Object.keys(this.localData).forEach((key) => {
        if (this.userType !== 'editor' && (key === 'memo' || key === 'first_language')) return;
        if (this.userType !== 'admin' && key === 'role_idx') return;
        const originalValue = this.basicData[key] !== undefined ? this.basicData[key] : null;
        const currentValue = this.localData[key];
        if (key === 'role_idx') {
          const original = originalValue === null ? null : Number(originalValue);
          const current = currentValue === null ? null : Number(currentValue);
          if (original !== current) changes[key] = current;
        } else if (originalValue !== currentValue) {
          changes[key] = currentValue;
        }
      });
      Object.keys(changes).forEach((key) => {
        this.$emit("update-field", { field: key, value: changes[key] });
      });
      this.$emit("save-changes");
    },
    formatDate(s) {
      if (!s) return "";
      return new Date(s).toLocaleString("ko-KR");
    },
    getLanguageName(code) {
      if (!code) return "";
      const lang = this.languageOptions.find((l) => l.code === code);
      return lang ? lang.name : code;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.bic {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__col-full {
    grid-column: 1 / -1;
  }
}
</style>
